import type {
  CallOutcome,
  CallType,
  EngagementVerificationType,
  InterestAssessmentType,
  State,
  TaskSubtype,
  TaskType,
} from "@prisma/client";
import type { SupportedTimezone } from "@marble/core/modules/timezones";
import type { AutodialerCallWindowBridge } from "@/app/_components/adminWorkflows/useAutodialerCallWindow";
import type { HydratedInsuranceInfoNetworkStatusAndGuardian } from "@/lib/modules/insurance/types";
import type { ClientSchedulingPreferenceEntry } from "@/lib/modules/students/scheduling-preferences/types";

export type WorkflowVoiceCallStartResult =
  | "completed"
  | "retryable_failure"
  | "terminal_failure";

export interface WorkflowSessionSummaryStats {
  /** Distinct autodialer dials (Twilio SID, else phone + start time). */
  callsMade: number;
  /** Queue tasks completed during the session. */
  tasksCompleted: number;
  converted: number;
  skipped: number;
  durationSeconds: number;
}

export type WorkflowSessionPrepCall = {
  id: number;
  startTime: Date;
  callType: CallType;
  callOutcome: CallOutcome | null;
  notes: string | null;
};

export type WorkflowSessionPrepRelatedClient = {
  id: number;
  firstName: string | null;
  lastName: string | null;
};

/**
 * Therapist-matching preferences the intake therapist captured when they
 * couldn't book recurring care themselves. Only surfaced on the scheduling
 * task types, where the client has no first appointment yet.
 */
export type WorkflowSessionPrepSchedulingPreferences = {
  blockedReason: string | null;
  preferences: ClientSchedulingPreferenceEntry[];
  /** "Mon (Morning); Tue (Evening)", or null when none were selected. */
  availability: string | null;
  constraints: string | null;
};

export type WorkflowSessionPrepData = {
  referral: {
    source: string | null;
    referredAt: Date | null;
    referredByName: string | null;
    schoolName: string | null;
    counselorName: string | null;
    referralNote: string | null;
    isSchoolReferral: boolean;
    engagementVerification: EngagementVerificationType | null;
    interestAssessment: InterestAssessmentType | null;
  };
  insurance: HydratedInsuranceInfoNetworkStatusAndGuardian | null;
  calls: WorkflowSessionPrepCall[];
  relatedClients: WorkflowSessionPrepRelatedClient[];
  /** Only included for first-appointment scheduling tasks. */
  schedulingPreferences?: WorkflowSessionPrepSchedulingPreferences | null;
  /**
   * Most recent intake progress note text. Only included for first-appointment
   * schedule and reschedule tasks — never therapy notes from later sessions.
   */
  intakeNote?: string | null;
};

export type WorkflowSessionSummaryReason = "user_stopped" | "queue_exhausted";

/**
 * Workflow state machine states.
 * User-facing task phases are prep / call:
 * - COUNTDOWN → prep
 * - CONNECTING + ACTIVE_CALL + POST_CALL_LOGGING → call
 * PAUSED is only used when the queue is exhausted.
 */
export type CallWorkflowState =
  | "IDLE"
  | "CONNECTING"
  | "ACTIVE_CALL"
  | "POST_CALL_LOGGING"
  | "COUNTDOWN"
  | "PAUSED";

/** Lightweight queue item for the autodialer: one row per task. */
export type CallWorkflowQueueItem = {
  id: number;
  taskType: TaskType;
  taskSubtype: TaskSubtype | null;
  ranking: number | null;
  dueAt: Date | null;
  createdAt: Date;
  /** Explicit scheduled-callback flag; required to land on `scheduledStack`. */
  isScheduled: boolean;
  clientName: string;
  targetUserId: number;
  studentId: number;
  skippedAt: Date | null;
  /** Student.state, else UserTask.state, else Eastern. */
  timezone: SupportedTimezone;
  /** State used to resolve `timezone` (Student.state, else UserTask.state). */
  clientState: State | null;
  /** Other household-group task ids. Empty when this task is not grouped. */
  groupedTaskIds?: number[];
  /** Other household-group student ids. Empty when this task is not grouped. */
  groupedStudentIds?: number[];
  /** Normalized resolved VoIP phone; used to flag unrelated same-number tasks. */
  resolvedContactPhone?: string | null;
  /** Queue clients with the same resolved phone who are not in this household. */
  unrelatedSameNumberClientNames?: string[];
};

export type CallWorkflowQueueResult = {
  queue: CallWorkflowQueueItem[];
  scheduledStack: CallWorkflowQueueItem[];
};

export type CallWorkflowTaskDetail = {
  id: number;
  taskType: TaskType;
  taskSubtype: TaskSubtype | null;
  ranking: number | null;
  dueAt: Date | null;
  targetUserId: number;
  clientName: string;
  notes: string | null;
  studentId: number | null;
  /**
   * True when intake is already scheduled or completed/attended — autodialer
   * should save converted=true and skip the conversion prompt.
   */
  inferConvertedFromIntake: boolean;
  /** State of the student — used to select the correct outbound Marble phone number. */
  studentState: State | null;
  studentUser: {
    id: number;
    firstName: string | null;
    lastName: string | null;
    phoneNumber: string | null;
  } | null;
  guardians: Array<{
    id: number;
    firstName: string | null;
    lastName: string | null;
    phoneNumber: string | null;
    isPrimary: boolean;
    /** State from the Guardian record — used to select the correct outbound Marble phone number. */
    state: State | null;
  }>;
};

/** Alias kept for callers that use the workflow-scoped name. */
export type WorkflowQueueItem = CallWorkflowQueueItem;

export interface WorkflowStartResult {
  userId: number;
  marbleAdminId: number;
  sessionId: string;
  queue: CallWorkflowQueueItem[];
  scheduledStack: CallWorkflowQueueItem[];
  completedCount: number;
  startedAt: string;
  /** Redis session version right after this write — pass back as
   * `expectedVersion` on the next sync so a stale writer (e.g. a tab that
   * lost session ownership without noticing yet) gets rejected instead of
   * silently clobbering a newer write. */
  version: number;
}

export interface WorkflowResumeResult extends WorkflowStartResult {
  restoredState: CallWorkflowState;
  queuePosition: number | null;
  currentTaskId: number | null;
  skippedTaskIds: number[];
  awaitingFirstCall: boolean;
  sessionBaselineCompletedCount: number;
  sessionBaselineSkippedCount: number;
  convertedCount: number;
  sessionSkippedCount: number;
  lastCallStartedAt?: string;
}

/** Ephemeral session stats persisted in Redis while the workflow is active. */
export interface WorkflowRedisSession {
  sessionId: string;
  startedAt: string;
  state: CallWorkflowState;
  queuePosition: number | null;
  currentTaskId: number | null;
  skippedTaskIds: number[];
  /** Unused; persisted for Redis session compatibility. */
  autoAdvance: boolean;
  /** True until the first outbound call of a new session starts. Legacy restore only. */
  awaitingFirstCall: boolean;
  completedCount: number;
  /** Unused; persisted for Redis session compatibility. */
  dailyGoal: number;
  queueSize: number;
  skippedCount: number;
  /** Daily completed count when this workflow session started (for session summary). */
  sessionBaselineCompletedCount: number;
  /** Skipped tasks already in the queue when this session started. */
  sessionBaselineSkippedCount: number;
  /** Conversions logged during this workflow session. */
  convertedCount: number;
  /** Tasks explicitly skipped by the user during this session (not pre-existing skips). */
  sessionSkippedCount: number;
  /** Inactivity anchor. Updated when call work starts and finishes. */
  lastCallStartedAt?: string;
  /** Monotonically incremented on every successful write; used as an
   * optimistic-concurrency check so two tabs that briefly both believe
   * they own the session can't silently clobber each other's writes. */
  version: number;
}

/** What a caller sends when persisting a session — the server stamps `version`. */
export type WorkflowRedisSessionInput = Omit<WorkflowRedisSession, "version">;

export type PendingTaskNavigation = {
  taskId: number;
  studentId: number;
  clientName: string;
} | null;

/** Workflow context shape */
export interface CallWorkflowContextValue {
  // State
  state: CallWorkflowState;
  /**
   * The state the workflow was in when PAUSE_WORKFLOW was dispatched.
   * Null when not paused.
   */
  stateBeforePause: CallWorkflowState | null;
  sessionId: string | null;
  queuePosition: number | null;
  currentTaskId: number | null;
  skippedSet: Set<number>;
  /** Tasks completed this session — optimistically hidden from the queue display before the next server poll. */
  completedTaskIds: Set<number>;
  /** All tasks completed this session, for session-summary reporting. Unlike completedTaskIds, not cleared on queue refetch. */
  sessionCompletedTaskIds: Set<number>;
  isLoading: boolean;
  error: string | null;
  isPanelVisible: boolean;
  /** Timestamp when workflow started (for duration calculation). */
  sessionStartedAt: Date | null;
  /** True once this tab started the session or won CLAIMING_PRIMARY. */
  isSessionOwner: boolean;
  /** Count of converted calls in this session. */
  convertedCount: number;
  /** Daily completed count when this workflow session started. */
  sessionBaselineCompletedCount: number;
  /** Skipped tasks already in the queue when this session started. */
  sessionBaselineSkippedCount: number;
  /** Tasks explicitly skipped by the user during this session (not pre-existing skips). */
  sessionSkippedCount: number;
  /** True once the current call log has been saved (before queue advance). */
  callLogSaved: boolean;
  pendingTaskNavigation: PendingTaskNavigation;

  // Data
  queue: WorkflowQueueItem[];
  /** Held scheduled calls for the current 7am–7am queue day. */
  scheduledStack: WorkflowQueueItem[];
  /** Scheduled call offered at a task transition, if any. */
  scheduledPromptTask: WorkflowQueueItem | null;
  /** Daily completed call count (includes calls before this session). */
  completedCount: number;

  // Auth context (not in reducer — set from provider props)
  agentPhoneNumber: string | null;
  adminId: number | null;

  // Actions
  startWorkflow: () => Promise<boolean>;
  stopWorkflow: (options?: StopWorkflowOptions) => Promise<boolean>;
  skipTask: (taskId: number) => void;
  unskipTask: (taskId: number) => void;
  setQueuePosition: (position: number | null) => void;
  setCurrentTaskId: (taskId: number | null) => void;
  transitionState: (newState: CallWorkflowState) => void;
  /** Advance to the next task (refreshes queue first). Pauses when none remain. */
  advanceQueue: () => Promise<boolean>;
  /**
   * Advance after a mistake cancel (draft log deleted). Does not complete
   * the task or increment the session completed count.
   */
  advanceQueueAfterMistakeCancel: () => Promise<void>;
  /** Skip the current task and advance (refreshes queue). Pauses when none remain. */
  skipCurrentAndAdvance: (taskId: number) => Promise<void>;
  /** Skip the current task, hanging up first when a live call is active. */
  skipCurrentTask: () => Promise<void>;
  /**
   * Switch the current task to a held scheduled call (prompt Complete now,
   * or Call now from the scheduled list).
   */
  takeScheduledCallNow: (taskId: number) => Promise<void>;
  /** Dismiss the scheduled-call prompt and continue the regular queue. */
  continuePastScheduledPrompt: () => Promise<void>;
  /**
   * Places the Twilio outbound call from a user click (required for mic/connect).
   * Requests VoIP browser permissions if they are not already granted.
   * Returns false when permissions are denied or the device is not ready yet.
   */
  startVoiceCall: () => Promise<boolean>;
  /** True once the task-specific outbound Marble caller ID has been resolved. */
  isOutboundCallerIdReady: boolean;
  setOutboundCallerIdReady: (ready: boolean) => void;
  /**
   * True while a call log submission for the current task is in flight.
   * A redial started before that submission resolves would let its
   * callback mark the new attempt already saved, hiding its outcome form.
   */
  isCallLogSubmitting: boolean;
  setIsCallLogSubmitting: (submitting: boolean) => void;
  /** Registers the active call card skip handler (hang-up + advance). */
  registerSkipCurrentTaskHandler: (
    handler: (() => Promise<void>) | null,
  ) => void;
  /** Registers handler to place the outbound VoIP call from Start call. */
  registerStartVoiceCallHandler: (
    handler: (() => Promise<WorkflowVoiceCallStartResult>) | null,
  ) => void;
  /** Registers handler to force-disconnect an in-progress call before stop. */
  registerEndActiveCallHandler: (
    handler: (() => Promise<string | null | void>) | null,
  ) => void;
  /**
   * True while the auto-missed household wrap-up is waiting to save sibling
   * complete/skip choices on "Go to next task".
   */
  householdWrapUpPending: boolean;
  /** Registers the auto-missed household wrap-up submitter. */
  registerHouseholdWrapUpSubmitHandler: (
    handler: (() => Promise<boolean>) | null,
  ) => void;
  /** Save pending household wrap-up radios. Returns false if save failed. */
  submitHouseholdWrapUp: () => Promise<boolean>;
  /** Show the workflow panel (when hidden due to "Back to nav"). */
  showPanel: () => void;
  /** Hide the workflow panel and restore left navigation. */
  hidePanel: () => void;
  /** Increment the converted count (called when a call results in a conversion). */
  incrementConverted: () => void;
  /** Count a finalized call log toward session + daily completed stats. Returns removed scheduled tasks for undo. */
  recordCompletedCallInSession: (options?: {
    isConverted?: boolean;
    completedTaskIds?: number[];
    convertedDelta?: number;
    /** False when this call was already counted as a dial (household wrap-up after an auto-saved voicemail/missed call). Defaults to true. */
    countsAsDial?: boolean;
  }) => WorkflowQueueItem[];
  /** Undo session stats when a saved call log is deleted as a mistake. */
  revertCompletedCallFromSession: (options?: {
    wasConverted?: boolean;
    convertedDelta?: number;
    completedTaskIds?: number[];
    skippedTaskIds?: number[];
    countsAsDial?: boolean;
    removedScheduledTasks?: WorkflowQueueItem[];
  }) => void;
  /** Mark the current call log as saved without advancing the queue. */
  markCallLogSaved: () => void;
  clearPendingTaskNavigation: () => void;
  /** Send session prep data to secondary tabs so they skip a network fetch. */
  broadcastPrepCache: (studentId: number, data: unknown) => void;
  /**
   * Bridge to the autodialer's popup call window — opened once per session
   * and reused for every call in the queue, so a reload of this tab never
   * disconnects a live call. If the BroadcastChannel goes silent, both sides
   * recreate it in place (the popup and Twilio Device stay up). See
   * `useAutodialerCallWindow`.
   */
  callWindow: AutodialerCallWindowBridge;
}

type WorkflowContextActions = Pick<
  CallWorkflowContextValue,
  | "startWorkflow"
  | "stopWorkflow"
  | "skipTask"
  | "unskipTask"
  | "setQueuePosition"
  | "setCurrentTaskId"
  | "transitionState"
  | "advanceQueue"
  | "advanceQueueAfterMistakeCancel"
  | "skipCurrentAndAdvance"
  | "skipCurrentTask"
  | "takeScheduledCallNow"
  | "continuePastScheduledPrompt"
  | "startVoiceCall"
  | "isOutboundCallerIdReady"
  | "setOutboundCallerIdReady"
  | "isCallLogSubmitting"
  | "setIsCallLogSubmitting"
  | "registerSkipCurrentTaskHandler"
  | "registerStartVoiceCallHandler"
  | "registerEndActiveCallHandler"
  | "householdWrapUpPending"
  | "registerHouseholdWrapUpSubmitHandler"
  | "submitHouseholdWrapUp"
  | "showPanel"
  | "hidePanel"
  | "incrementConverted"
  | "recordCompletedCallInSession"
  | "revertCompletedCallFromSession"
  | "markCallLogSaved"
  | "clearPendingTaskNavigation"
  | "broadcastPrepCache"
  | "isSessionOwner"
  // Non-reducer fields passed from provider props:
  | "agentPhoneNumber"
  | "adminId"
  | "callWindow"
>;

/** Synchronous workflow state managed by the client reducer */
export type WorkflowReducerState = Omit<
  CallWorkflowContextValue,
  keyof WorkflowContextActions
> & {
  /** Set when workflow starts/resumes from server auth lookup. */
  sessionMarbleAdminId: number | null;
  /** True until the first outbound call of a new session starts. Legacy restore only. */
  awaitingFirstCall: boolean;
  /** ISO inactivity anchor, updated when call work starts and finishes. */
  lastCallStartedAt: string | null;
  /** Latest authoritative Redis version observed by this tab. */
  sessionVersion: number;
};

/** Reducer action types */
export type WorkflowAction =
  | {
      type: "START_WORKFLOW";
      payload: {
        sessionId: string;
        marbleAdminId: number;
        queue: WorkflowQueueItem[];
        scheduledStack: WorkflowQueueItem[];
        completedCount: number;
        sessionStartedAt: Date;
        version?: number;
      };
    }
  | {
      type: "RESTORE_WORKFLOW";
      payload: {
        sessionId: string;
        marbleAdminId: number;
        queue: WorkflowQueueItem[];
        scheduledStack: WorkflowQueueItem[];
        completedCount: number;
        state: CallWorkflowState;
        queuePosition: number | null;
        currentTaskId: number | null;
        skippedTaskIds: number[];
        awaitingFirstCall: boolean;
        sessionStartedAt: Date;
        sessionBaselineCompletedCount: number;
        sessionBaselineSkippedCount: number;
        convertedCount: number;
        sessionSkippedCount: number;
        isPanelVisible?: boolean;
        lastCallStartedAt?: string | null;
        version?: number;
      };
    }
  | { type: "STOP_WORKFLOW" }
  | { type: "PAUSE_WORKFLOW" }
  | { type: "RESUME_WORKFLOW" }
  | { type: "TRANSITION_STATE"; payload: { state: CallWorkflowState } }
  | {
      type: "SET_QUEUE";
      payload: {
        queue: WorkflowQueueItem[];
        scheduledStack?: WorkflowQueueItem[];
        pendingSkipTaskIds?: number[];
        pendingUnskipTaskIds?: number[];
      };
    }
  | { type: "SET_QUEUE_POSITION"; payload: { position: number | null } }
  | { type: "SET_CURRENT_TASK_ID"; payload: { taskId: number | null } }
  | { type: "INCREMENT_COMPLETED_COUNT" }
  | { type: "DECREMENT_COMPLETED_COUNT" }
  | { type: "SKIP_TASK"; payload: { taskId: number } }
  | { type: "UNSKIP_TASK"; payload: { taskId: number } }
  | { type: "SET_LOADING"; payload: { loading: boolean } }
  | { type: "SET_ERROR"; payload: { error: string | null } }
  | { type: "SET_SESSION_VERSION"; payload: { version: number } }
  | { type: "SET_PANEL_VISIBILITY"; payload: { visible: boolean } }
  | { type: "INCREMENT_CONVERTED" }
  | { type: "DECREMENT_CONVERTED" }
  | { type: "SET_CALL_LOG_SAVED"; payload: { saved: boolean } }
  | {
      type: "SET_PENDING_TASK_NAVIGATION";
      payload: {
        navigation: PendingTaskNavigation;
      };
    }
  | {
      type: "SET_SCHEDULED_PROMPT";
      payload: { task: WorkflowQueueItem | null };
    }
  | { type: "REMOVE_FROM_SCHEDULED_STACK"; payload: { taskId: number } }
  | { type: "RESTORE_TO_SCHEDULED_STACK"; payload: { task: WorkflowQueueItem } }
  | { type: "MARK_TASK_COMPLETED"; payload: { taskId: number } }
  | { type: "UNMARK_TASK_COMPLETED"; payload: { taskId: number } }
  | {
      type: "RECLASSIFY_EXPIRED_SCHEDULED";
      payload: {
        queue: WorkflowQueueItem[];
        scheduledStack: WorkflowQueueItem[];
      };
    }
  | { type: "APPLY_SYNCED_STATE"; payload: { state: WorkflowReducerState } };

/** Workflow state machine reducer initial state */
export const INITIAL_WORKFLOW_STATE: WorkflowReducerState = {
  state: "IDLE",
  sessionId: null,
  queuePosition: null,
  currentTaskId: null,
  skippedSet: new Set(),
  isLoading: false,
  error: null,
  queue: [],
  completedCount: 0,
  sessionMarbleAdminId: null,
  stateBeforePause: null,
  awaitingFirstCall: false,
  isPanelVisible: true,
  sessionStartedAt: null,
  convertedCount: 0,
  sessionBaselineCompletedCount: 0,
  sessionBaselineSkippedCount: 0,
  sessionSkippedCount: 0,
  callLogSaved: false,
  pendingTaskNavigation: null,
  scheduledStack: [],
  scheduledPromptTask: null,
  completedTaskIds: new Set<number>(),
  sessionCompletedTaskIds: new Set<number>(),
  lastCallStartedAt: null,
  sessionVersion: 0,
};

export type WorkflowSessionEndReason = "stopped" | "inactivity_timeout";

export interface StopWorkflowOptions {
  showSessionSummary?: boolean;
  sessionEndReason?: WorkflowSessionSummaryReason | "inactivity_timeout";
  /** When set, the server force-disconnects this admin browser call leg on stop. */
  adminCallSid?: string;
}
